/**
 * celebrations-card.tsx — STP-044 client-facing celebration surface
 * (R2.3 sub-step 2).
 *
 * Renders 0..5 unacknowledged celebrations inside the client `/dashboard`.
 * Each celebration shows a trigger-specific icon + label + the ISO date,
 * plus an "Acknowledge" button that flips `acknowledged=true` server-side
 * and removes the row from the list optimistically.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ackCelebration, type PendingCelebration } from '@/lib/api-client';

interface Props {
  accessToken: string;
  clientId: string;
  celebrations: PendingCelebration[];
}

const ICON_FOR_TRIGGER: Record<string, string> = {
  fulfillment_streak: '↗',
  weekly_focus_hit: '✦',
  consecutive_weekly_streak: '⌁',
  phase_gate: '◆',
  graduation: '★',
  custom: '✶',
};

export function CelebrationsCard({
  accessToken,
  clientId,
  celebrations,
}: Props): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const [pending, setPending] = useState<PendingCelebration[]>(celebrations);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (pending.length === 0) return null;

  const dateFormatter = new Intl.DateTimeFormat(i18n.language === 'de' ? 'de-DE' : 'en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const handleAck = async (id: string): Promise<void> => {
    setBusyId(id);
    try {
      await ackCelebration(accessToken, clientId, id);
      setPending((rows) => rows.filter((r) => r.id !== id));
    } catch {
      // Failure is silent — the row stays + a future refresh can retry.
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      data-testid="celebrations-card"
      className="rounded-2xl border border-bone-3 bg-bone p-5 shadow-sm"
    >
      <header className="mb-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink/60">
          {t('dashboard.celebrations.eyebrow')}
        </p>
        <h2 className="font-display text-lg font-semibold text-ink">
          {t('dashboard.celebrations.title')}
        </h2>
      </header>
      <ul className="space-y-3">
        {pending.map((c) => {
          // CodeFix-V1 #42 — surface the celebration to screen readers.
          // The trigger label + date were already visible; the parent
          // list item had no accessible name so SR users heard only
          // the emoji icon (which is aria-hidden).
          const labelText = t(`dashboard.celebrations.label.${c.trigger_type}`, {
            defaultValue: c.trigger_type,
          });
          const dateText = dateFormatter.format(new Date(c.triggered_at));
          return (
          <li
            key={c.id}
            data-testid={`celebration-${c.trigger_type}`}
            className="flex items-start gap-3 rounded-xl bg-bone-2 p-3"
            aria-label={`${labelText} — ${dateText}`}
          >
            <span
              aria-hidden="true"
              className="font-display text-xl leading-none text-[hsl(var(--flame,28_100%_50%))]"
            >
              {ICON_FOR_TRIGGER[c.trigger_type] ?? '✷'}
            </span>
            <div className="flex-1">
              <p className="text-sm font-medium text-ink">
                {t(`dashboard.celebrations.label.${c.trigger_type}`, {
                  defaultValue: c.trigger_type,
                })}
              </p>
              <p className="mt-1 text-xs text-ink/60">
                {dateFormatter.format(new Date(c.triggered_at))}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                void handleAck(c.id);
              }}
              disabled={busyId === c.id}
              className="rounded-md border border-bone-3 px-3 py-1 text-xs font-medium text-ink hover:bg-bone-3 disabled:opacity-50"
            >
              {t('dashboard.celebrations.acknowledge')}
            </button>
          </li>
          );
        })}
      </ul>
    </section>
  );
}
